import * as React from "react";
import { ChevronDownIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) */
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

interface DateTimePickerProps {
  date: string;
  time: string;
  onDateChange: (isoDate: string) => void;
  onTimeChange: (time: string) => void;
  dateId?: string;
  timeId?: string;
  dateLabel?: React.ReactNode;
  timeLabel?: React.ReactNode;
  className?: string;
  timeDisabled?: boolean;
}

function toDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return isNaN(d.getTime()) ? undefined : d;
}

function toISO(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function DatePicker({ value, onChange, placeholder = "Selecionar data", className, id }: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = toDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          data-empty={!selected}
          className={cn(
            "justify-between text-left font-normal data-[empty=true]:text-muted-foreground",
            className,
          )}
        >
          <span>{selected ? format(selected, "dd/MM/yyyy", { locale: ptBR }) : placeholder}</span>
          <ChevronDownIcon className="opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          captionLayout="dropdown"
          onSelect={(day) => {
            if (day) {
              onChange(toISO(day));
              setOpen(false);
            }
          }}
          defaultMonth={selected}
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );
}

function DateTimePicker({
  date,
  time,
  onDateChange,
  onTimeChange,
  dateId,
  timeId,
  dateLabel = "Data",
  timeLabel = "Horário",
  className,
  timeDisabled = false,
}: DateTimePickerProps) {
  return (
    <div className={cn("flex gap-2 items-end flex-wrap", className)}>
      <div className="flex flex-col gap-1">
        <Label className="text-xs" htmlFor={dateId}>
          {dateLabel}
        </Label>
        <DatePicker
          id={dateId}
          value={date}
          onChange={onDateChange}
          className="w-[11rem]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs" htmlFor={timeId}>
          {timeLabel}
        </Label>
        <Input
          id={timeId}
          type="time"
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          disabled={timeDisabled}
          className="w-[8rem] appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
    </div>
  );
}

export { DatePicker, DateTimePicker };
